import { useEffect } from 'react';

/**
 * Custom hook for managing document head meta tags for SEO
 * Supports dynamic page titles, descriptions, and structured data
 */
export function useSEO({ 
  title, 
  description, 
  type = 'website',
  image,
  article = null 
}) {
  useEffect(() => {
    // Set document title
    const defaultTitle = 'Handshake Wiki - Free Business Encyclopedia | Companies, Executives & Investors';
    const fullTitle = title 
      ? `${title} | Handshake Business Wiki` 
      : defaultTitle;
    document.title = fullTitle;

    // Update or create meta tags
    const setMetaTag = (name, content, isProperty = false) => {
      const attr = isProperty ? 'property' : 'name';
      let element = document.querySelector(`meta[${attr}="${name}"]`);
      if (!element) {
        element = document.createElement('meta');
        element.setAttribute(attr, name);
        document.head.appendChild(element);
      }
      element.setAttribute('content', content);
    };

    // Primary meta
    if (description) {
      setMetaTag('description', description);
      setMetaTag('og:description', description, true);
      setMetaTag('twitter:description', description);
    }

    // Title meta
    setMetaTag('og:title', fullTitle, true);
    setMetaTag('twitter:title', fullTitle);

    // Type
    setMetaTag('og:type', type, true);

    // Image
    if (image) {
      setMetaTag('og:image', image, true);
      setMetaTag('twitter:image', image);
    }

    // Article specific meta
    if (article) {
      if (article.author) {
        setMetaTag('article:author', article.author, true);
      }
      if (article.publishedTime) {
        setMetaTag('article:published_time', article.publishedTime, true);
      }
      if (article.modifiedTime) {
        setMetaTag('article:modified_time', article.modifiedTime, true);
      }
      if (article.section) {
        setMetaTag('article:section', article.section, true);
      }
    }

    // Cleanup function to reset title on unmount
    return () => {
      document.title = defaultTitle;
    };
  }, [title, description, type, image, article]);
}

/**
 * Generate JSON-LD structured data for a wiki article/company page
 */
export function generateArticleSchema(page) {
  if (!page) return null;
  
  return {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": page.title,
    "description": page.summary || `Learn about ${page.title} on Handshake, the business encyclopedia wiki.`,
    "datePublished": page.created_at,
    "dateModified": page.updated_at,
    "author": {
      "@type": "Organization",
      "name": "Handshake Wiki"
    },
    "publisher": {
      "@type": "Organization",
      "name": "Handshake",
      "logo": {
        "@type": "ImageObject",
        "url": "/logo.png"
      }
    },
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": `https://handshake.wiki/wiki/${page.slug}`
    }
  };
}

/**
 * Generate JSON-LD structured data for a company/organization
 */
export function generateOrganizationSchema(page) {
  if (!page || !page.infobox) return null;
  
  const infobox = page.infobox;
  
  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": page.title,
    "description": page.summary,
    "foundingDate": infobox.Founded,
    "address": infobox.Headquarters ? {
      "@type": "PostalAddress",
      "addressLocality": infobox.Headquarters
    } : undefined,
    "tickerSymbol": infobox["Stock Symbol"],
    "url": page.social_links?.website
  };
}

export default useSEO;
